import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom crisis marker icon with SVG icons
const createCrisisIcon = (severity, type) => {
  const colors = {
    5: '#D32F2F', // Critical - Deep Red
    4: '#FF6100', // High - NRC Orange  
    3: '#ED6C02', // Medium - Warning Orange
    2: '#FFA726', // Low - Light Orange
    1: '#2E7D32', // Minimal - Green
  };
  
  // SVG icons for different crisis types
  const icons = {
    earthquake: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M14.73 10L15 10l.27.1.09.06.06.09.1.27v4l-.1.27-.06.09-.09.06L15 15h-1.73l-.27-.1-.09-.06-.06-.09-.1-.27v-4l.1-.27.06-.09.09-.06.27-.1h1.46M7 3l4 5-4 5V8.5c-3 0-5.5 2.5-5.5 5.5 0 1.39.53 2.65 1.38 3.62l-1.48 1.48A7.49 7.49 0 010 14c0-4.14 3.36-7.5 7.5-7.5V3m10 0v4.5c3 0 5.5 2.5 5.5 5.5a5.48 5.48 0 01-1.38 3.62l1.48 1.48A7.49 7.49 0 0024 14c0-4.14-3.36-7.5-7.5-7.5V3l-4 5 4 5V8"/></svg>`,
    conflict: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M6.92 6.92L5.5 5.5 4 7l1.5 1.5L7 7l-.08-.08M15 1l-4 4v6l-2.17 2.17A2.99 2.99 0 006 16c0 1.66 1.34 3 3 3 .84 0 1.58-.34 2.12-.88L19 10.15V4l4-4h-8m4 9.88L10.88 22A2.98 2.98 0 019 23a3 3 0 01-3-3c0-1.06.54-1.98 1.37-2.52l.26.26 1.42-1.42-.26-.26c.54-.83 1.46-1.37 2.52-1.37 1.66 0 3 1.34 3 3 0 .84-.34 1.58-.88 2.12L15 17.88"/></svg>`,
    flood: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M12 3.77L11.25 4.61C11.25 4.61 9.97 6.06 8.68 7.94C7.39 9.82 6 12.07 6 14.23A6 6 0 0012 20.23A6 6 0 0018 14.23C18 12.07 16.61 9.82 15.32 7.94C14.03 6.06 12.75 4.61 12.75 4.61L12 3.77M12 6.9C12.44 7.42 12.84 7.85 13.21 8.28C14.08 9.21 14.85 10.17 15.44 11.29C16.04 12.4 16.5 13.71 16.5 14.23A4.5 4.5 0 0112 18.73A4.5 4.5 0 017.5 14.23C7.5 13.71 7.96 12.4 8.56 11.29C9.15 10.17 9.92 9.21 10.79 8.28C11.16 7.85 11.56 7.42 12 6.9Z"/></svg>`,
    epidemic: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M19.15 8c-1.35-.24-2.59-.89-3.54-1.85S13.99 3.96 13.75 2.61c-.11-.65-.72-1.08-1.37-.96s-1.08.72-.96 1.37c.34 1.88 1.24 3.6 2.55 4.91 1.31 1.31 3.03 2.21 4.91 2.55.65.12 1.25-.31 1.37-.96s-.31-1.25-.96-1.37l-.14-.15M7 12.88c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3m8.46 4.87L12 21.21l-3.46-3.46a5 5 0 01-7.08 0 5 5 0 010-7.08l3.46-3.46L8.38 3.75a5 5 0 017.08 0 5 5 0 010 7.08L12 14.29l3.46 3.46a5 5 0 010 7.08 5 5 0 01-7.08 0"/></svg>`,
    famine: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M12 2a10 10 0 0110 10 10 10 0 01-10 10A10 10 0 012 12 10 10 0 0112 2m0 2a8 8 0 00-8 8 8 8 0 008 8 8 8 0 008-8 8 8 0 00-8-8m0 2l1.5 4.5L18 12l-4.5 1.5L12 18l-1.5-4.5L6 12l4.5-1.5L12 6m0 4.1L11.4 12l1.6.6.6 1.6.6-1.6 1.6-.6-1.6-.6-.6-1.6-.6 1.6z"/></svg>`,
    refugee_crisis: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M12 5.5A3.5 3.5 0 0115.5 9a3.5 3.5 0 01-3.5 3.5A3.5 3.5 0 018.5 9 3.5 3.5 0 0112 5.5M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7m-2 15h4v2h-4v-2m0 3h4v2h-4v-2z"/></svg>`,
    economic_crisis: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M11 8h2v8h-2V8m-4 4h2v4H7v-4m8 2h2v2h-2v-2m-4-6a9 9 0 00-9 9 9 9 0 009 9 9 9 0 009-9 9 9 0 00-9-9m0-2a11 11 0 0111 11 11 11 0 01-11 11A11 11 0 011 12 11 11 0 0112 1z"/></svg>`,
    other: `<svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M13 14h-2v-4h2m0 8h-2v-2h2m-1-18A10 10 0 002 12a10 10 0 0010 10 10 10 0 0010-10A10 10 0 0012 2z"/></svg>`
  };

  const typeClass = type ? type.replace('_', '-') : 'other';

  return L.divIcon({
    html: `
      <div class="crisis-marker ${typeClass}" style="
        background-color: ${colors[severity] || '#6b7280'};
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 12px rgba(0,0,0,0.3);
        position: relative;
      ">
        <div class="crisis-icon" style="width: 20px; height: 20px; display: flex; align-items: center; justify-content: center;">
          ${icons[type] || icons.other}
        </div>
        <span class="severity-badge">${severity}</span>
      </div>
    `,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20]
  });
};

const LeafletMap = () => {
  const [crises, setCrises] = useState([]);
  const [earthquakes, setEarthquakes] = useState([]);
  const [showEarthquakes, setShowEarthquakes] = useState(true);
  const [showSatellite, setShowSatellite] = useState(false);
  const [mapCenter] = useState([20, 0]); // Initial center
  const [mapZoom] = useState(2); // Initial zoom

  // Fetch crisis data
  useEffect(() => {
    const fetchCrises = async () => {
      try {
        const response = await fetch('/api/crises');
        if (response.ok) {
          // Check if response is actually JSON
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            const data = await response.json();
            setCrises(data);
            return;
          }
        }
        throw new Error('API not available or returning HTML');
      } catch (error) {
        console.error('Failed to fetch crises, using mock data:', error);
        // Fallback to mock data for demo
        setCrises([
          {
            id: 1,
            title: 'Haiti Earthquake Response',
            type: 'earthquake',
            severity: 5,
            location: 'Port-au-Prince, Haiti',
            latitude: 18.5944,
            longitude: -72.3074,
            affected_population: 150000,
            status: 'active'
          },
          {
            id: 2,
            title: 'Syrian Refugee Crisis',
            type: 'conflict',
            severity: 4,
            location: 'Aleppo, Syria',
            latitude: 36.2021,
            longitude: 37.1343,
            affected_population: 250000,
            status: 'active'
          },
          {
            id: 3,
            title: 'Bangladesh Floods',
            type: 'flood',
            severity: 3,
            location: 'Dhaka, Bangladesh',
            latitude: 23.8103,
            longitude: 90.4125,
            affected_population: 500000,
            status: 'monitoring'
          },
          {
            id: 4,
            title: 'Somalia Drought',
            type: 'famine',
            severity: 4,
            location: 'Mogadishu, Somalia',
            latitude: 2.0469,
            longitude: 45.3182,
            affected_population: 300000,
            status: 'active'
          },
          {
            id: 5,
            title: 'Ukraine Conflict',
            type: 'conflict',
            severity: 5,
            location: 'Kyiv, Ukraine',
            latitude: 50.4501,
            longitude: 30.5234,
            affected_population: 1000000,
            status: 'active'
          }
        ]);
      }
    };

    fetchCrises();
  }, []);

  // Fetch earthquake data
  useEffect(() => {
    const fetchEarthquakes = async () => {
      try {
        const response = await fetch('/api/feeds/earthquakes?limit=20');
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            const data = await response.json();
            setEarthquakes(data);
            return;
          }
        }
        throw new Error('Earthquake API not available');
      } catch (error) {
        console.error('Failed to fetch earthquakes, using mock data:', error);
        // Fallback to mock earthquake data
        setEarthquakes([
          {
            id: 1,
            magnitude: 6.2,
            location: 'Off the coast of Japan',
            coordinates: [142.369, 37.916],
            time: new Date().toISOString()
          },
          {
            id: 2,
            magnitude: 5.8,
            location: 'California, USA',
            coordinates: [-118.2437, 34.0522],
            time: new Date(Date.now() - 3600000).toISOString()
          },
          {
            id: 3,
            magnitude: 7.1,
            location: 'Chile',
            coordinates: [-70.6693, -33.4489],
            time: new Date(Date.now() - 7200000).toISOString()
          }
        ]);
      }
    };

    fetchEarthquakes();
  }, []);

  // Map tile layers
  const streetTileUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const satelliteTileUrl = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
  
  const tileAttribution = showSatellite 
    ? 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  return (
    <div className="global-map-container">
      <div className="map-controls">
        <button 
          className={`map-control-btn ${showSatellite ? 'active' : ''}`}
          onClick={() => setShowSatellite(!showSatellite)}
          title="Toggle Satellite View"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5 20L7.5 17.5L5.91 15.91L3 16L4 20L5 20M20.04 13C20.18 13.69 20.25 14.42 20.25 15.17C20.25 18.22 18.94 20.92 16.88 22.93L15.47 21.5C17.13 19.85 18.17 17.65 18.17 15.17C18.17 14.59 18.12 14.03 18 13.5L20.04 13M11 4C11.64 4 12.26 4.07 12.86 4.2L10.16 6.9C10.28 6.96 10.41 7 10.55 7.03L13 4.6C15.03 5.65 16.5 7.53 17 9.77L14.38 12.39C14.5 11.93 14.55 11.46 14.55 11C14.55 8.82 12.73 7 10.55 7C10.43 7 10.31 7 10.2 7.03L7.65 9.58C7.16 8.97 6.78 8.26 6.59 7.46L9.12 4.93C9.68 4.65 10.32 4.5 11 4.5V4M4.83 7.65L6.22 6.26C5.61 7.15 5.17 8.2 5 9.36L3 11.35C3.29 9.96 3.93 8.7 4.83 7.65M8.5 12.5L10 11L8.61 9.61C8.5 10.06 8.45 10.53 8.45 11C8.45 11.47 8.5 11.93 8.61 12.39L10 11L8.5 12.5Z"/></svg>
          <span className="ml-1">Satellite</span>
        </button>
        <button 
          className={`map-control-btn ${showEarthquakes ? 'active' : ''}`}
          onClick={() => setShowEarthquakes(!showEarthquakes)}
          title="Toggle Earthquake Layer"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14.73 10L15 10l.27.1.09.06.06.09.1.27v4l-.1.27-.06.09-.09.06L15 15h-1.73l-.27-.1-.09-.06-.06-.09-.1-.27v-4l.1-.27.06-.09.09-.06.27-.1h1.46M7 3l4 5-4 5V8.5c-3 0-5.5 2.5-5.5 5.5 0 1.39.53 2.65 1.38 3.62l-1.48 1.48A7.49 7.49 0 010 14c0-4.14 3.36-7.5 7.5-7.5V3m10 0v4.5c3 0 5.5 2.5 5.5 5.5a5.48 5.48 0 01-1.38 3.62l1.48 1.48A7.49 7.49 0 0024 14c0-4.14-3.36-7.5-7.5-7.5V3l-4 5 4 5V8"/></svg>
          <span className="ml-1">Earthquakes</span>
        </button>
        <div className="legend">
          <h4>Crisis Severity</h4>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: '#D32F2F'}}></span>
            Critical (5)
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: '#FF6100'}}></span>
            High (4)
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: '#ED6C02'}}></span>
            Medium (3)
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: '#FFA726'}}></span>
            Low (2)
          </div>
        </div>
      </div>

      <MapContainer 
        center={mapCenter} 
        zoom={mapZoom} 
        style={{ height: '100%', width: '100%' }}
        className="leaflet-map"
      >
        <TileLayer
          url={showSatellite ? satelliteTileUrl : streetTileUrl}
          attribution={tileAttribution}
        />
        
        {/* Crisis Markers */}
        {crises.map(crisis => (
          crisis.latitude && crisis.longitude && (
            <Marker
              key={crisis.id}
              position={[crisis.latitude, crisis.longitude]}
              icon={createCrisisIcon(crisis.severity, crisis.type)}
            >
              <Popup>
                <div className="crisis-popup-content">
                  <h3>{crisis.title}</h3>
                  <div className="crisis-details">
                    <p><strong>Type:</strong> {crisis.type.replace('_', ' ')}</p>
                    <p><strong>Severity:</strong> {crisis.severity}/5</p>
                    <p><strong>Location:</strong> {crisis.location}</p>
                    <p><strong>Affected:</strong> {crisis.affected_population?.toLocaleString()} people</p>
                    <p><strong>Status:</strong> 
                      <span className={`status-badge ${crisis.status}`}>
                        {crisis.status}
                      </span>
                    </p>
                  </div>
                  <div className="crisis-actions">
                    <button className="btn-primary" onClick={() => {
                      window.location.href = `/crisis/${crisis.id}`;
                    }}>
                      View Details
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          )
        ))}

        {/* Earthquake Markers */}
        {showEarthquakes && earthquakes.map((eq, index) => (
          eq.coordinates && eq.coordinates.length >= 2 && (
            <Marker
              key={`earthquake-${index}`}
              position={[eq.coordinates[1], eq.coordinates[0]]}
              icon={L.divIcon({
                html: `
                  <div style="
                    background-color: ${eq.magnitude >= 7 ? '#dc2626' : eq.magnitude >= 6 ? '#ea580c' : eq.magnitude >= 5 ? '#f59e0b' : '#fbbf24'};
                    width: ${Math.min(40, Math.max(12, eq.magnitude * 4))}px;
                    height: ${Math.min(40, Math.max(12, eq.magnitude * 4))}px;
                    border-radius: 50%;
                    border: 2px solid white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    font-weight: bold;
                    color: white;
                    opacity: 0.8;
                  ">
                    ${eq.magnitude}
                  </div>
                `,
                className: 'earthquake-marker',
                iconSize: [Math.min(40, Math.max(12, eq.magnitude * 4)), Math.min(40, Math.max(12, eq.magnitude * 4))],
                iconAnchor: [Math.min(20, Math.max(6, eq.magnitude * 2)), Math.min(20, Math.max(6, eq.magnitude * 2))]
              })}
            >
              <Popup>
                <div>
                  <h4>Earthquake</h4>
                  <p><strong>Magnitude:</strong> {eq.magnitude}</p>
                  <p><strong>Location:</strong> {eq.location}</p>
                  <p><strong>Time:</strong> {new Date(eq.time).toLocaleString()}</p>
                </div>
              </Popup>
            </Marker>
          )
        ))}
      </MapContainer>
    </div>
  );
};

export default LeafletMap;